import {
  Avatar,
  Box,
  createStyles,
  Drawer,
  Flex,
  Group,
  Progress,
  Text,
  Badge,
} from "@mantine/core";
import React from "react";
import { IconUsers } from "@tabler/icons-react";

const useStyles = createStyles(() => ({
  drawer: {
    ".mantine-Drawer-content": {
      backgroundColor: "#f5f5f5",
    },
    ".mantine-Drawer-title": {
      fontSize: 20,
      fontWeight: 700,
      fontFamily: "Inter",
      color: "#000",
      width: "100%",
    },
    ".mantine-Drawer-header": {
      backgroundColor: "#fff",
    },
  },
  container: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    padding: "16px",
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "#E5E5E5",
    "::-webkit-scrollbar": { width: "10px" },
    "::-webkit-scrollbar-track": { background: "#f0f0f0", borderRadius: "10px" },
    "::-webkit-scrollbar-thumb": { background: "#888", borderRadius: "10px" },
    "::-webkit-scrollbar-thumb:hover": { background: "#E5E5E5" },
  },
  staffRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "16px 0",
    borderBottom: "1px solid #F3F4F6",
    "&:last-child": { borderBottom: "none" },
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    backgroundColor: "#EFF6FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: 14,
    fontWeight: 600,
    color: "#3B82F6",
    fontFamily: "Inter",
  },
  statBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    fontFamily: "Inter",
  },
}));

const StaffTasksDrawer = ({ opened, close, staffSummary }) => {
  const { classes } = useStyles();

  const totalTasks = staffSummary?.reduce((sum, s) => sum + (s.weeklyStats?.total || 0), 0) || 0;
  const totalCompleted = staffSummary?.reduce((sum, s) => sum + (s.weeklyStats?.completed || 0), 0) || 0;
  const totalInProgress = staffSummary?.reduce((sum, s) => sum + (s.weeklyStats?.inProgress || 0), 0) || 0;
  const totalPending = totalTasks - totalCompleted - totalInProgress;

  return (
    <Drawer
      size="35%"
      offset={8}
      radius="md"
      zIndex={1000}
      position="right"
      title={
        <Group spacing={10}>
          <IconUsers size={22} color="#3B82F6" />
          <Text ff="Inter" fw={700} fz={18} color="#1F2937">
            Staff Tasks ({staffSummary?.length || 0} staff)
          </Text>
        </Group>
      }
      opened={opened}
      className={classes.drawer}
      onClose={close}
      overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
    >
      {/* Summary bar */}
      <Box px={16} py={12} mb={8}>
        <Text ff="Inter" fz={12} fw={600} color="#9CA3AF" mb={6} style={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
          This Week
        </Text>
        <Flex gap={16} wrap="wrap">
          <Flex align="center" gap={6}>
            <Box style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#3B82F6" }} />
            <Text ff="Inter" fz={13} color="#6B7280">{totalTasks} total</Text>
          </Flex>
          <Flex align="center" gap={6}>
            <Box style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#10B981" }} />
            <Text ff="Inter" fz={13} color="#6B7280">{totalCompleted} completed</Text>
          </Flex>
          {totalInProgress > 0 && (
            <Flex align="center" gap={6}>
              <Box style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#3B82F6" }} />
              <Text ff="Inter" fz={13} color="#6B7280">{totalInProgress} in progress</Text>
            </Flex>
          )}
          {totalPending > 0 && (
            <Flex align="center" gap={6}>
              <Box style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#F59E0B" }} />
              <Text ff="Inter" fz={13} color="#F59E0B" fw={500}>{totalPending} pending</Text>
            </Flex>
          )}
        </Flex>
      </Box>

      <Box className={classes.container}>
        {staffSummary?.length > 0 ? (
          staffSummary.map((staff) => {
            const ws = staff.weeklyStats || {};
            const weekTotal = ws.total || 0;
            const weekCompleted = ws.completed || 0;
            const weekInProgress = ws.inProgress || 0;
            const weekPending = ws.pending || 0;
            const pct = weekTotal > 0 ? Math.round((weekCompleted / weekTotal) * 100) : 0;
            const barColor = weekTotal === 0 ? "gray" : pct === 100 ? "green" : pct >= 50 ? "teal" : pct >= 25 ? "yellow" : "orange";

            return (
              <Box key={staff.id} className={classes.staffRow}>
                {/* Avatar with login indicator */}
                <Box style={{ position: "relative", flexShrink: 0 }}>
                  <Avatar
                    size={38}
                    radius="xl"
                    src={staff.photo || null}
                    alt={staff.name}
                    color="blue"
                    styles={{
                      root: { flexShrink: 0 },
                      placeholder: { backgroundColor: "#EFF6FF", color: "#3B82F6", fontFamily: "Inter", fontSize: 14, fontWeight: 600 },
                    }}
                  >
                    {staff.name?.charAt(0)?.toUpperCase() || "?"}
                  </Avatar>
                  <Box style={{
                    position: "absolute", bottom: -1, right: -1,
                    width: 10, height: 10, borderRadius: "50%",
                    backgroundColor: staff.loggedIn ? "#10B981" : "#D1D5DB",
                    border: "2px solid #fff",
                  }} />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Flex align="center" gap={6} style={{ flex: 1 }}>
                      <Text ff="Inter" fz={14} fw={600} color="#374151" lineClamp={1}>
                        {staff.name}
                      </Text>
                      {staff.position && (
                        <Text ff="Inter" fz={11} color="#9CA3AF" lineClamp={1}>· {staff.position}</Text>
                      )}
                      {!staff.loggedIn && (
                        <Badge variant="outline" color="gray" size="xs" radius="sm" ff="Inter"
                          styles={{ root: { textTransform: "none", fontWeight: 500, borderColor: "#E5E7EB" } }}>
                          Offline
                        </Badge>
                      )}
                    </Flex>
                    {weekTotal > 0 ? (
                      <Text ff="Inter" fz={14} fw={700} color={pct === 100 ? "#10B981" : "#374151"} ml={8}>
                        {weekCompleted}/{weekTotal}
                      </Text>
                    ) : staff.total > 0 ? (
                      <Text ff="Inter" fz={12} fw={500} color="#F59E0B" ml={8}>No task completed</Text>
                    ) : (
                      <Text ff="Inter" fz={12} color="#9CA3AF" ml={8}>No tasks assigned</Text>
                    )}
                  </Flex>
                  {weekTotal > 0 && (
                    <Progress value={pct} color={barColor} size="sm" radius="xl" />
                  )}
                  <Flex gap={8} mt={8} wrap="wrap">
                    {weekCompleted > 0 && (
                      <Badge variant="light" color="green" size="xs" radius="sm" ff="Inter">
                        {weekCompleted} completed
                      </Badge>
                    )}
                    {weekInProgress > 0 && (
                      <Badge variant="light" color="blue" size="xs" radius="sm" ff="Inter">
                        {weekInProgress} in progress
                      </Badge>
                    )}
                    {weekPending > 0 && (
                      <Badge variant="light" color="yellow" size="xs" radius="sm" ff="Inter">
                        {weekPending} pending
                      </Badge>
                    )}
                    {staff.missedDaysCount > 0 && (
                      <Badge variant="light" color="red" size="xs" radius="sm" ff="Inter">
                        {staff.missedDaysCount} missed days
                      </Badge>
                    )}
                  </Flex>
                </Box>
              </Box>
            );
          })
        ) : (
          <Flex align="center" justify="center" py={60}>
            <Text ff="Inter" fz={14} color="#9CA3AF">No staff tasks assigned</Text>
          </Flex>
        )}
      </Box>
    </Drawer>
  );
};

export default StaffTasksDrawer;
